import type { StoreV2 } from '@libs/common-types';

export const stringifyStoreId = (storeId?: string | number) =>
    storeId ? `${storeId.toString().padStart(4, '0')}` : undefined;

export const stripStoreId = (storeId?: string) =>
    storeId?.length && storeId?.length > 1 ? storeId?.replace(/^0+/, '') : storeId;

/**
 * Create URL friendly slug from city string
 * `City Name //=> city-name`
 */
const getCitySlug = (city = ''): string => city.normalize().split(' ').join('-').toLowerCase();

export const getStoreDetailsPathSegment = ({ City, StoreNumber }: Pick<StoreV2, 'City' | 'StoreNumber'>): string => {
    const citySlug = getCitySlug(City);
    return `${citySlug}-store${stringifyStoreId(StoreNumber)}.html`;
};

/**
 * ## Constructs a URL pathname for store details using the provided store properties.
 *
 * This function takes a subset of the store properties: `CountryAbbreviation`, `StateProvinceAbbreviation`, `City`, and `StoreNumber`.
 * It performs the following steps:
 *
 * 1. Converts the `City` value into a URL-friendly slug using the `getCitySlug` function.
 * 2. Converts the `CountryAbbreviation` and `StateProvinceAbbreviation` to lowercase.
 * 3. Constructs and returns the pathname in the following format:
 *    `/stores/{country}/{state}/{citySlug}-store{StoreNumber}.html`
 */
export const getStoreDetailsPathname = (
    store: Pick<StoreV2, 'CountryAbbreviation' | 'StateProvinceAbbreviation' | 'City' | 'StoreNumber'>
): string => {
    const { CountryAbbreviation, StateProvinceAbbreviation } = store;
    return `/stores/${CountryAbbreviation?.toLocaleLowerCase()}/${StateProvinceAbbreviation?.toLowerCase()}/${getStoreDetailsPathSegment(
        store
    )}`;
};
